import React, {ComponentType} from 'react';

import {WithRouterProps} from 'react-router';

import {
  DocumentSubscribedStatusButton,
  DocumentSubscribersButton,
} from 'app/general/doc-status';
import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {SPPage} from 'app/general/structures/SPPage';
import {ModernLinkTabs} from 'app/general/widgets/ModernTabs';
import {useSweetTeamDetail} from 'app/teams/slice';
import {getTeamDetailPageURL, TEAMS_HOME} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {DeleteTeamButton} from './components/DeleteTeamButton';
import {TeamPageHeader} from './components/TeamPageHeader';
import {TeamFoldersSubPage} from './TeamFoldersSubPage';
import {TeamMembersSubPage} from './TeamMembersSubPage';
import {TeamPoliciesSubPage} from './TeamPoliciesSubPage';
import {TeamProceduresSubPage} from './TeamProceduresSubPage';
import {TeamProcessesSubPage} from './TeamProcessesSubPage';
import {TeamProductsSubPage} from './TeamProductsSubPage';
import {TeamQuizzesSubPage} from './TeamQuizzesSubPage';
import {TeamSignoffsSubPage} from './TeamSignoffsSubPage';

import type {TeamSerializedType} from 'sweet-types';

type Props = WithRouterProps<
  {
    teamid: string;
    slug: string;
  },
  {
    tab: string;
  }
>;

export const TeamDetailPage = ({
  router,
  params: {teamid, slug},
}: Props): JSX.Element => {
  const {team, refetch} = useSweetTeamDetail(teamid);
  const [fetchCounter, setFetchCounter] = React.useState(0);

  // hack used to remount (therefore refetch) subpage data when team is refetched
  const refetchTeam = () => {
    refetch();
  };

  // hack used to remount (therefore refetch) subpage data when team is updated
  // used by RTK-Query's refetch mechanism
  React.useEffect(() => {
    setFetchCounter((c) => c + 1);
  }, [team]);

  const tabs: Array<{
    label: string;
    typeSlug: string;
    counter: (team: TeamSerializedType) => number | null;
    ListComponent: ComponentType<{
      team: TeamSerializedType;
      onFetch: () => unknown;
    }>;
  }> = [
    {
      label: gt('Members'),
      typeSlug: 'users',
      counter: (t) => t.num_managers + t.num_teammates + t.num_readers,
      ListComponent: TeamMembersSubPage,
    },
    {
      label: gt('Procedures'),
      typeSlug: 'procedures',
      counter: (t) => t.number_of_procedures || 0,
      ListComponent: TeamProceduresSubPage,
    },
    {
      label: gt('Processes'),
      typeSlug: 'processes',
      counter: (t) => t.number_of_processes,
      ListComponent: TeamProcessesSubPage,
    },
    {
      label: gt('Policies'),
      typeSlug: 'policies',
      counter: (t) => t.number_of_policies,
      ListComponent: TeamPoliciesSubPage,
    },
    {
      label: gt('Folders'),
      typeSlug: 'folders',
      counter: (t) => t.number_of_folders,
      ListComponent: TeamFoldersSubPage,
    },
    {
      label: gt('Sign Offs'),
      typeSlug: 'signoffs',
      counter: () => null,
      ListComponent: TeamSignoffsSubPage,
    },
    {
      label: gt('Products'),
      typeSlug: 'products',
      counter: () => null,
      ListComponent: TeamProductsSubPage,
    },
    {
      label: gt('Quizzes'),
      typeSlug: 'quizzes',
      counter: () => null,
      ListComponent: TeamQuizzesSubPage,
    },
  ];

  return team == null ? (
    <DetailLoadingPage />
  ) : (
    <SPPage
      pageTitle={team.name}
      breadcrumbProps={{
        docName: team.name != null ? team.name : '',
      }}
      actionArea={
        <div className="d-flex">
          <div className="mr-2">
            <DocumentSubscribedStatusButton
              document={team}
              contentType={'team'}
            />
          </div>
          <div className="mr-2">
            <DocumentSubscribersButton
              document={team}
              contentType="team"
              canEdit={team.can_edit}
            />
          </div>
          {team.can_edit === true ? (
            <DeleteTeamButton
              team={team}
              teamDeletedCallback={() => router.push(TEAMS_HOME)}
            />
          ) : null}
        </div>
      }
    >
      <TeamPageHeader team={team} />
      <ModernLinkTabs
        tabs={tabs.map(({label, typeSlug, counter, ListComponent}) => ({
          label: (
            <span>
              {label}
              {counter(team) != null ? (
                <span
                  className={`ml-2 badge badge-${
                    counter(team) ? 'secondary' : 'light'
                  }`}
                >
                  {counter(team)}
                </span>
              ) : null}
            </span>
          ),
          to: getTeamDetailPageURL(teamid, slug, typeSlug),
          content: () => (
            <ListComponent
              key={fetchCounter}
              team={team}
              onFetch={refetchTeam}
            />
          ),
        }))}
      />
    </SPPage>
  );
};
